/**
 * UI v2 number/money formatters (SPEC §5.7).
 * Ports of the mockup helpers in components.jsx (fmtMoney/fmtInt) and
 * pnl.jsx:7–21 (fmtM/fmtK/splitNum). Real minus glyph "−", tabular
 * numerals. Every dollar amount renders inside <span className="bs-money">.
 *
 * Written in the READER's language (uiv2 7c, `lib/locale.ts`): English as the
 * mockup draws it, Canadian French ("1 234,50 $"), Arabic with Latin digits
 * inside a left-to-right isolate.
 */
import { formatLocale, ltrFigure, readerLocale, stripIsolates } from './locale';


export interface FmtMoneyOptions {
  /** Prefix positive values with "+". */
  sign?: boolean;
  /** Format the magnitude only (never negative). */
  abs?: boolean;
  /** Fraction digits (default 2). */
  decimals?: number;
  /** Currency symbol (default "$"). */
  currency?: string;
  /**
   * The language to write in; the reader's by default. A machine writer (a
   * PDF, whose fonts are WinAnsi) passes `'en'`.
   */
  locale?: string;
}

/**
 * The placeholder every formatter here falls back to.
 *
 * `apiGet<T>` is an unchecked cast, so a field the server has not shipped yet
 * arrives as `undefined` rather than as a type error — and the deploy
 * recreates web and api containers non-atomically, guaranteeing a window each
 * release where exactly that happens. `undefined.toLocaleString()` throws
 * inside render and the ErrorBoundary takes the page. A dash is a worse
 * number and a far better page.
 *
 * Zero is NOT absent and must never render as this: "no tips collected" and
 * "we could not load tips" are different facts.
 */
const ABSENT = '—';

/** No-break space: French keeps "1 234,50" and its "$" on one line. */
const NBSP = '\u00a0';

function groups(v: number, decimals: number, locale?: string): string {
  return v.toLocaleString(formatLocale(locale), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Where the reader's language puts a currency symbol: after the number in
 * French ("1 234,50 $"), before it in English and Arabic ("$1,234.50").
 */
function symbolAfter(locale?: string): boolean {
  return readerLocale(locale) === 'fr';
}

/** A magnitude with its sign and symbol, in the reader's order, isolated in RTL. */
function money(signChar: string, digits: string, currency: string, locale?: string): string {
  const text = symbolAfter(locale) ? `${signChar}${digits}${NBSP}${currency}` : `${signChar}${currency}${digits}`;
  return ltrFigure(text, locale);
}

/** "$1,234.50" · "−$42.00" · "+$10.00" (sign) · "$10.00" (abs); "1 234,50 $" in French. */
export function fmtMoney(n: number, opts: FmtMoneyOptions = {}): string {
  if (!Number.isFinite(n)) return ABSENT;
  const { sign = false, abs = false, decimals = 2, currency = '$', locale } = opts;
  const digits = groups(Math.abs(n), decimals, locale);
  if (sign && n > 0) return money('+', digits, currency, locale);
  if (n < 0 && !abs) return money('−', digits, currency, locale);
  return money('', digits, currency, locale);
}

/**
 * A setting's value in the reader's locale — "37.5", "37,5" in French (uiv2
 * R3c: an overtime threshold, a billers band, a tax rate). `maxDecimals` is the
 * column's scale: 2 for the thresholds, 3 for a tax rate (QST is 9.975).
 */
export function fmtSetting(n: number, locale: string, maxDecimals = 2): string {
  if (!Number.isFinite(n)) return ABSENT;
  return new Intl.NumberFormat(formatLocale(locale), { maximumFractionDigits: maxDecimals }).format(n);
}

/**
 * A plain figure at a fixed precision, in the reader's language: 1234.5 →
 * "1,234.5" ("1 234,5" in French). Replaces `x.toFixed(n)` wherever the result
 * is READ — `toFixed` writes "1234.5" in every language, with no grouping.
 */
export function fmtNum(n: number, decimals = 0, opts: FmtPtsOptions = {}): string {
  if (!Number.isFinite(n)) return ABSENT;
  const text = new Intl.NumberFormat(formatLocale(), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(n));
  // A value that rounds to zero carries no sign: "−0.0" reads as a small loss.
  const zero = Number(text.replace(/[^0-9]/g, '')) === 0;
  const sign = zero ? '' : n < 0 ? '−' : opts.sign ? '+' : '';
  return ltrFigure(`${sign}${text}`);
}

export interface FmtPtsOptions {
  /** Prefix a positive value with "+" (a change, not a share). */
  sign?: boolean;
}

/**
 * A figure that is ALREADY in percent points: 20.8 → "20.8%" ("20,8 %" in
 * French). `fmtPercent` takes a fraction (0.208); most of the app's shares and
 * changes arrive from the server as points, and were printed with
 * `${x.toFixed(1)}%`.
 */
export function fmtPts(n: number, decimals = 1, opts: FmtPtsOptions = {}): string {
  // `fmtPercent` is a dash for a non-finite value, and a dash has no digits: no sign.
  const text = stripIsolates(fmtPercent(Math.abs(n) / 100, decimals));
  const zero = Number(text.replace(/[^0-9]/g, '')) === 0;
  const sign = zero ? '' : n < 0 ? '−' : opts.sign ? '+' : '';
  return ltrFigure(`${sign}${text}`);
}

/**
 * Money as the API sends it — a `numeric` string ("1234.56"), a number, or
 * nothing — in the reader's language; a dash when absent or unparseable.
 * Four tax components each carried a copy of this (en-CA, "CA$" in one
 * duplicate modal, USD in two others) until uiv2 7c.
 */
export function fmtMoneyValue(val: string | number | null | undefined): string {
  // `parseFloat` of "", "null", "undefined" or junk is NaN, which `fmtMoney` dashes.
  return fmtMoney(parseFloat(String(val)));
}

/** Grouped integer, e.g. 12345 → "12,345" ("12 345" in French). */
export function fmtInt(n: number, locale?: string): string {
  if (!Number.isFinite(n)) return ABSENT;
  return ltrFigure(n.toLocaleString(formatLocale(locale)), locale);
}

/**
 * One decimal place, e.g. 0.208 → "20.8%". Ported out of the Sales tab's
 * MenuSection and what is now OperationsSection (P2, Task 9) — both
 * defined this identical
 * one-liner independently, which is the same `shadows`-token drift CLAUDE.md
 * documents, caught at two copies instead of three.
 */
/**
 * `decimals` is a parameter because precision is a property of the figure,
 * not of percentages. A refund rate lives between about 0.05% and 0.5%
 * (SL-28) and renders as a flat "0.1%" across most of that range at one
 * decimal place, while a payment-mix share at two would be noise. Default
 * unchanged, so no existing call site moves.
 */
export function fmtPercent(n: number, decimals = 1): string {
  if (!Number.isFinite(n)) return ABSENT;
  const text = new Intl.NumberFormat(formatLocale(), {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(n);
  // Intl writes Arabic's percent with its own direction marks; the isolate
  // replaces them, and the minus is the real one like every other figure here.
  return ltrFigure(stripIsolates(text).replace('-', '−'));
}

export interface FmtMOptions {
  sign?: boolean;
  decimals?: number;
}

/**
 * Money with a leading real-minus for negatives: -1234.5 → "−$1,234.50".
 * `fmtMoney` with `sign` once zero is excluded: zero is "$0.00", never "+$0.00".
 */
export function fmtM(n: number, opts: FmtMOptions = {}): string {
  const { sign = false, decimals = 2 } = opts;
  return fmtMoney(n, { sign, decimals });
}

/** A compact magnitude ("65", "1.3") with its unit letter, in the reader's order. */
function compactMoney(n: number, value: number, fraction: number, unit: string): string {
  const digits = groups(value, fraction);
  // French spaces the unit from the number and closes it with the symbol: "65 k$".
  const body = symbolAfter() ? `${digits}${unit ? NBSP + unit : NBSP}$` : `$${digits}${unit}`;
  return ltrFigure(`${n < 0 ? '−' : ''}${body}`);
}

/**
 * Money divided down to a unit for a chart AXIS or tile: `(12_400, 1_000, 1,
 * 'K')` → "$12.4K" ("12,4 k$" in French — French writes the thousands unit
 * lower-case). The caller chooses the unit; this places it, the symbol and
 * the sign. `(v, 1, 0, '')` is a whole, grouped amount.
 */
export function fmtScaledMoney(n: number, divisor: number, decimals: number, unit: string): string {
  if (!Number.isFinite(n)) return ABSENT;
  const scaled = Number((Math.abs(n) / divisor).toFixed(decimals));
  return compactMoney(n, scaled, decimals, symbolAfter() && unit === 'K' ? 'k' : unit);
}

/** Compact thousands for axis labels: 65330 → "$65k", -3000 → "−$3k"; "65 k$" in French. */
export function fmtK(n: number): string {
  return compactMoney(n, Number((Math.abs(n) / 1000).toFixed(0)), 0, 'k');
}

/** Compact money for KPI/stat display: 1_250_000 → "$1.3M", 12_000 → "$12K", 640 → "$640". Real minus for negatives. */
export function fmtCompact(n: number): string {
  if (n == null || Number.isNaN(n)) return ABSENT;
  const abs = Math.abs(n);
  const k = symbolAfter() ? 'k' : 'K';
  if (abs >= 1_000_000) return compactMoney(n, Number((abs / 1_000_000).toFixed(1)), 1, 'M');
  if (abs >= 1_000) return compactMoney(n, Number((abs / 1_000).toFixed(0)), 0, k);
  return compactMoney(n, Number(abs.toFixed(0)), 0, '');
}

export interface SplitNum {
  neg: boolean;
  /** Grouped integer part, e.g. "1,234" ("1 234" in French). */
  int: string;
  /** Two-digit fraction without the separator, e.g. "50". */
  dec: string;
  /** The reader's decimal separator: "." or ",". */
  decimal: string;
  /** The currency symbol follows the number (French). */
  symbolAfter: boolean;
}

/** Split a value for serif KPI numerals: -1234.5 → { neg: true, int: "1,234", dec: "50" }. */
export function splitNum(n: number): SplitNum {
  // Round to cents FIRST, then split — otherwise flooring the integer while
  // rounding the fraction independently drifts (12.995 → "12" + "00" = $12.00).
  const rounded = Math.round(Math.abs(n) * 100) / 100;
  const intPart = Math.floor(rounded);
  const int = intPart.toLocaleString(formatLocale());
  const dec = Math.round((rounded - intPart) * 100)
    .toString()
    .padStart(2, '0');
  const decimal = new Intl.NumberFormat(formatLocale()).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.';
  return { neg: n < 0, int, dec, decimal, symbolAfter: symbolAfter() };
}
